/**
 * assets/myaiga-cart.js
 * ---------------------------------------------------------------------------
 * myAiga✓ — Cart integration.
 *
 * Responsibilities:
 *  1. Keep window.myAigaCart.attributes in sync with the live Shopify cart,
 *     so the `cartAttributes` adapter in myaiga-core.js always has fresh
 *     data (req #14, #16).
 *  2. Push the resolved membership status onto cart attributes
 *     (myaiga_status, myaiga_member, myaiga_source, myaiga_version) so a
 *     Shopify Function, discount, or Checkout UI Extension can read it at
 *     checkout time (req #17). This NEVER claims a discount was applied —
 *     it only exposes state for those systems to act on.
 *  3. Provide window.myAigaCartApi.addToCart/updateCart wrappers that any
 *     custom myAiga UI can call, on top of Shopify's standard /cart/*.js
 *     endpoints (no third-party app dependency).
 *  4. Detect cart changes made by the theme's OWN add/update/remove calls
 *     (which use plain fetch to /cart/add.js, /cart/update.js, etc. with no
 *     custom events) via a narrow, additive fetch tap — never blocking or
 *     altering the original request/response — and re-resolve membership +
 *     re-fetch cart totals so the banner/savings stay correct without a
 *     full page reload (req #15). No polling is used.
 * ---------------------------------------------------------------------------
 */
(function (window, document) {
  "use strict";

  var cfg = window.myAigaConfig || {};
  if (!cfg.enabled || !cfg.features || !cfg.features.cartMembershipEnabled) return;

  var VERSION = "1.1";
  var syncing = false; // guards against our own attribute-sync fetch retriggering itself

  function log() {
    if (cfg.debugMode && window.console) {
      console.log.apply(console, ["[myAiga:cart]"].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("cart.js request failed: " + res.status);
        return res.json();
      })
      .catch(function (err) {
        log("fetchCart failed (non-fatal):", err.message);
        return null;
      });
  }

  function setCartState(cart) {
    if (!cart) return;
    window.myAigaCart = window.myAigaCart || {};
    window.myAigaCart.attributes = cart.attributes || {};
    window.myAigaCart.itemCount = cart.item_count;
    window.myAigaCart.totalPrice = cart.total_price;
    window.myAigaCart.items = cart.items || [];
    window.myAigaCart.discountCodes = cart.discount_codes || [];
  }

  // ---------------------------------------------------------------------
  // Service fee product waiver (Theme Settings → myAiga✓ → Handling fee →
  // Fee enforcement → "Theme — auto-remove a Service Fee product for
  // members"). Only does anything when that specific enforcement mode is
  // selected AND a Service fee product is configured — otherwise a no-op.
  // This is real cart enforcement (removes the line item via the standard
  // cart API), not just display copy, but only works when the fee is
  // modeled as an actual product in the cart.
  // ---------------------------------------------------------------------
  var feeWaiverPromise = null;
  function waiveServiceFeeIfMember() {
    if (feeWaiverPromise) return feeWaiverPromise;

    var fee = cfg.handlingFee || {};
    if (!fee.enabled || !fee.serviceFeeVariantId) return Promise.resolve();

    var cartItems = (window.myAigaCart && window.myAigaCart.items) || [];
    var planIds = (cfg.checkoutDiscount && cfg.checkoutDiscount.planVariantIds) || [];
    var feeVariantId = String(fee.serviceFeeVariantId);
    // myAiga used to have two different fee products in the store
    // (service-fee-handling-fee and service-fee). Treat BOTH as fee lines so
    // an older/app-created fee cannot coexist with the theme fee.
    var feeHandles = {
      'service-fee-handling-fee': true,
      'service-fee': true
    };
    var feeLines = cartItems.filter(function (item) {
      return String(item.variant_id) === feeVariantId || feeHandles[String(item.handle || '').toLowerCase()];
    });
    var feeLine = feeLines[0];

    // A membership plan selected in THIS cart is a member checkout state,
    // even before payment grants the customer's permanent membership tag.
    var hasSelectedPlan = cartItems.some(function (item) {
      return planIds.indexOf(String(item.variant_id)) !== -1 &&
        !!item.selling_plan_allocation;
    });

    var confirmedMember = false;
    if (window.myAiga && typeof window.myAiga.getStatus === 'function') {
      confirmedMember = window.myAiga.getStatus() === 'member';
    }
    var memberState = confirmedMember || hasSelectedPlan;

    // A confirmed existing member must never keep a membership subscription
    // line in the shopping cart. Membership is already active on the account,
    // so remove any stale monthly/yearly plan and the $0 discount marker.
    var cleanupLines = [];
    if (confirmedMember) {
      var knownPlanIds = planIds.map(function (id) { return String(id); });
      cartItems.forEach(function (item) {
        if (knownPlanIds.indexOf(String(item.variant_id)) !== -1) cleanupLines.push(item);
      });
      var markerVariantId = cfg.handlingFee && cfg.handlingFee.discountMarkerVariantId ? String(cfg.handlingFee.discountMarkerVariantId) : '';
      cartItems.forEach(function (item) {
        if (markerVariantId && String(item.variant_id) === markerVariantId) cleanupLines.push(item);
      });
    }

    // Never create a fee-only cart. The fee belongs to a non-empty normal
    // customer order, not to an empty cart.
    var hasNonFeeItem = cartItems.some(function (item) {
      return String(item.variant_id) !== feeVariantId &&
        !feeHandles[String(item.handle || '').toLowerCase()];
    });

    // -------------------------------------------------------------------
    // SERVER-SIDE ENFORCEMENT (cfg.discountEngine === "function")
    //
    // The "myAiga member pricing" Shopify Function discounts the fee line to
    // zero for tagged members at checkout. So the fee line must simply EXIST
    // for every cart that has something in it — members included — and the
    // theme must never add or remove it based on a membership status that may
    // not have resolved yet. That removes the add-then-remove race that could
    // otherwise leave a member charged $10 when the removal request failed.
    //
    // Stale membership-plan and marker lines are still cleaned up for
    // confirmed members: that's cart hygiene, not pricing, and getting it
    // wrong costs nobody money.
    // -------------------------------------------------------------------
    if (cfg.discountEngine === 'function') {
      var serverOps = [];

      if (confirmedMember && cleanupLines.length) {
        serverOps = cleanupLines.slice();
      }

      var needsFeeLine = hasNonFeeItem && !feeLine;

      if (!serverOps.length && !needsFeeLine) return Promise.resolve();

      feeWaiverPromise = serverOps.reduce(function (promise, line) {
        return promise.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          }).then(function (res) {
            if (!res.ok) throw new Error('stale line removal failed: ' + res.status);
            return res.json();
          });
        });
      }, Promise.resolve())
        .then(function () {
          if (!needsFeeLine) return null;
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: feeVariantId, quantity: 1 })
          }).then(function (res) {
            if (!res.ok) throw new Error('service fee add failed: ' + res.status);
            return fetchCart();
          });
        })
        .then(function (cart) {
          if (cart) {
            setCartState(cart);
            window.dispatchEvent(new CustomEvent('myaiga:service-fee-updated'));
          }
          log('function mode: fee line present, discount applied server-side');
        })
        .catch(function (err) {
          log('function-mode cart sync failed (non-fatal):', err.message);
        })
        .finally(function () { feeWaiverPromise = null; });

      return feeWaiverPromise;
    }

    if (memberState) {
      var linesToRemove = feeLines.slice();
      cleanupLines.forEach(function (line) {
        if (linesToRemove.indexOf(line) === -1) linesToRemove.push(line);
      });
      if (!linesToRemove.length) return Promise.resolve();
      // Remove every known fee-product line by its real Shopify line key.
      // For confirmed members this also removes stale membership-plan and
      // marker lines, leaving the account on normal products only.
      feeWaiverPromise = linesToRemove.reduce(function (promise, line) {
        return promise.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          }).then(function (res) {
            if (!res.ok) throw new Error('service fee removal failed: ' + res.status);
            return res.json();
          });
        });
      }, Promise.resolve()).then(function (cart) {
        setCartState(cart);
        log('removed member fee/stale membership lines:', linesToRemove.length);
      }).catch(function (err) {
        log('service fee removal failed (non-fatal):', err.message);
      }).finally(function () { feeWaiverPromise = null; });
      return feeWaiverPromise;
    }

    // NON-MEMBER ENFORCEMENT: if there is a normal order and no membership
    // plan, the real $10 Service Fee product MUST be in the Shopify cart.
    // This intentionally does not depend on the old "auto re-add" toggle;
    // that toggle only described mid-session membership-loss behavior and
    // must not prevent the normal non-member fee from being charged.
    if (hasNonFeeItem && !feeLine) {
      feeWaiverPromise = fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: feeVariantId, quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('service fee add failed: ' + res.status);
          return res.json();
        })
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          setCartState(cart);
          log('added $10 service fee for non-member cart');
          window.dispatchEvent(new CustomEvent('myaiga:service-fee-updated'));
        })
        .catch(function (err) {
          log('service fee add failed (non-fatal):', err.message);
        })
        .finally(function () { feeWaiverPromise = null; });
      return feeWaiverPromise;
    }

    return Promise.resolve();
  }

  // ---------------------------------------------------------------------
  // Checkout discount code (Theme Settings → myAiga✓ → Checkout discount
  // code). Uses the checkout discount query parameter (?discount=CODE)
  // during the final cart-to-checkout handoff.
  // displayed member price — the one piece theme Liquid genuinely cannot
  // do on its own (req #17/#41). Finds the standard Shopify "checkout"
  // submit control(s) on the page (cart page and cart drawer both use the
  // same name="checkout" convention) and, when eligible, sends the
  // customer to /checkout?discount=CODE instead of the default form
  // submission — the cart contents are unaffected either way, since
  // /checkout reflects the current cart session regardless of how it's
  // reached.
  // ---------------------------------------------------------------------
  function cartContainsPlanProduct() {
    var d = cfg.checkoutDiscount || {};
    var planIds = d.planVariantIds || [];
    if (!planIds.length) return false;
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    return items.some(function (item) {
      return planIds.indexOf(String(item.variant_id)) !== -1;
    });
  }

  // Picks the discount code matching whichever plan is actually in the
  // cart right now — Monthly and Yearly can have different codes (e.g.
  // different %, different Shopify discount configuration). Falls back to
  // the single generic code for stores with only one plan / one code.
  function resolveDiscountCodeForCart() {
    var d = cfg.checkoutDiscount || {};
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    var byVariant = d.planDiscountCodes || {};
    for (var i = 0; i < items.length; i++) {
      var code = byVariant[String(items[i].variant_id)];
      if (code) return code;
    }
    return d.code || "";
  }

  function isEligibleForCheckoutDiscount() {
    var d = cfg.checkoutDiscount || {};
    var code = resolveDiscountCodeForCart();
    if (!d.enabled || !code) return false;

    // SAME-ORDER MEMBERSHIP: a customer buying a membership in this cart is
    // not a "member" yet, so checking getStatus() here would incorrectly
    // block the discount. The matching MYAIGA_MONTHLY10 / MYAIGA_YEARLY10
    // Shopify discount is restricted to ONE-TIME PURCHASE products, which
    // automatically excludes the membership subscription itself.
    // Therefore a membership plan in the cart is explicitly eligible.
    if (cartContainsPlanProduct()) {
      log("checkout discount eligible — membership plan is in this cart:", code);
      return true;
    }

    if (d.applyTo === "everyone") return true;
    return !!window.myAiga && window.myAiga.getStatus() === "member";
  }

  function shouldInterceptCheckout() {
    var d = cfg.checkoutDiscount || {};
    var fee = cfg.handlingFee || {};
    // Intercept whenever there's real async cart state that could still be
    // in flight — either the discount mechanism or the fee waiver. If
    // neither is configured, there's nothing to wait for and the button
    // is left completely alone (native Shopify behavior).
    return !!d.enabled || fee.source === "theme_product_removal";
  }

  function bindCheckoutButtons() {
    // V9: checkout discount is handled only by the server-rendered cart button
    // and the global capture-phase handler in layout/theme.liquid.
    return;
  }

  // ---------------------------------------------------------------------
  // Legacy auto-apply disabled in V9 — Shopify's native /discount/{code} endpoint actually
  // attaches the discount to the cart/checkout SESSION (not just a URL
  // param on one link), so it shows in the real cart totals immediately
  // and persists into checkout no matter how the customer gets there
  // (the cart's own Checkout button, Buy it now, Shop Pay, PayPal
  // Express, etc). The `bindCheckoutButtons` mechanism above only ever
  // covered one specific click target — this covers all of them, because
  // the discount is genuinely part of the cart, not injected at the last
  // click. This is a full navigation (Shopify's own requirement for this
  // endpoint), so it's only triggered once per actual code change, never
  // repeatedly.
  // ---------------------------------------------------------------------
  // SAME-ORDER discount session
  // ---------------------------------------------------------------------
  // A membership plan is added to the cart first (with its Shopify selling
  // plan). We then apply the REAL Shopify discount to the cart session and
  // return to /cart. This is deliberately done BEFORE checkout rather than
  // intercepting the Checkout button. That means Shopify's native Checkout,
  // cart drawer, and checkout session all see the same discount state.
  // ---------------------------------------------------------------------
  var applyingDiscount = false;
  function getDesiredDiscountCode() {
    var d = cfg.checkoutDiscount || {};
    if (!d.enabled) return '';
    var items = (window.myAigaCart && window.myAigaCart.items) || [];
    var byVariant = d.planDiscountCodes || {};
    for (var i = 0; i < items.length; i++) {
      var code = byVariant[String(items[i].variant_id)];
      if (code) return String(code);
    }

    // EXISTING MEMBER: use the server-rendered Shopify customer state first.
    // This is intentionally independent of the asynchronous myAiga resolver,
    // so a signed-in member receives the benefit even while app scripts are
    // still loading. AiTrillion Membership V2 supplies the monthly/yearly
    // customer tag after successful membership activation.
    var serverCustomer = window.myAigaCustomer || {};
    if (serverCustomer.myAigaMember) {
      if (serverCustomer.myAigaMemberDiscountCode) return String(serverCustomer.myAigaMemberDiscountCode);
      if (serverCustomer.myAigaMemberPlan === 'yearly') return String(d.codeYearly || 'MYAIGA_YEARLY10');
      if (serverCustomer.myAigaMemberPlan === 'monthly') return String(d.codeMonthly || 'MYAIGA_MONTHLY10');
      return String(d.memberCode || d.code || '');
    }

    // Secondary app-resolved path for stores where membership is supplied by
    // an external adapter instead of a Shopify customer tag.
    if (window.myAiga && window.myAiga.getStatus && window.myAiga.getStatus() === 'member') {
      var customer = (window.myAiga.getCustomer && window.myAiga.getCustomer()) || {};
      var tags = customer.tags || [];
      var tagText = tags.join(',').toLowerCase();
      var yearlyTag = 'myaigamembersyearly';
      var monthlyTag = 'myaigamembersmonthly';
      if (cfg.aiTrillionMembership) {
        yearlyTag = String((cfg.aiTrillionMembership.yearly || {}).customerTag || yearlyTag).toLowerCase();
        monthlyTag = String((cfg.aiTrillionMembership.monthly || {}).customerTag || monthlyTag).toLowerCase();
      }
      if (yearlyTag && tagText.indexOf(yearlyTag) !== -1) return String(d.codeYearly || 'MYAIGA_YEARLY10');
      if (monthlyTag && tagText.indexOf(monthlyTag) !== -1) return String(d.codeMonthly || 'MYAIGA_MONTHLY10');
      return String(d.memberCode || d.code || '');
    }
    return '';
  }

  function getAppliedDiscountCode() {
    var cart = window.myAigaCart || {};
    var codes = cart.discountCodes || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].applicable !== false && codes[i].code) {
        return String(codes[i].code);
      }
    }
    return '';
  }

  function clearDiscountAttempt() {
    try { sessionStorage.removeItem('myaiga_discount_attempt'); } catch (e) {}
  }

  function maybeAutoApplyDiscount() {
    if (applyingDiscount) return Promise.resolve();
    // This runs globally so a signed-in member gets their real discount and
    // fee waiver even when they open checkout from a cart drawer or another
    // page, not only from /cart.

    var desired = getDesiredDiscountCode();
    var applied = getAppliedDiscountCode();

    // No membership plan in this cart. Never add a member discount.
    if (!desired) {
      clearDiscountAttempt();
      return Promise.resolve();
    }

    // Correct code is already attached to the actual Shopify cart session.
    if (applied.toUpperCase() === desired.toUpperCase()) {
      clearDiscountAttempt();
      log('real Shopify cart discount confirmed:', desired);
      return Promise.resolve();
    }

    // A different myAiga code is present (e.g. monthly -> yearly). Re-apply
    // the correct one. A one-time guard prevents an endless loop if Shopify
    // rejects the endpoint for any reason.
    var attempt = '';
    try { attempt = sessionStorage.getItem('myaiga_discount_attempt') || ''; } catch (e) {}
    if (attempt.toUpperCase() === desired.toUpperCase()) {
      log('discount endpoint already attempted this session; not looping:', desired);
      return Promise.resolve();
    }

    // V15: the plan selector establishes the discount immediately after a
    // plan is chosen. Do not launch a second redirect here; this listener is
    // now a verifier/fallback only.
    log('discount not yet visible on cart; waiting for plan-selector discount session:', desired);
    return Promise.resolve();
  }

  // ---------------------------------------------------------------------
  // Attribute sync — exposes membership state to checkout-side systems.
  // Merges with existing attributes so we never clobber other apps' data.
  // ---------------------------------------------------------------------
  function syncMembershipAttributes() {
    if (!cfg.features.checkoutMembershipEnabled) return Promise.resolve();
    if (!window.myAiga) return Promise.resolve();

    var status = window.myAiga.getStatus();
    var source = window.myAiga.getSource ? window.myAiga.getSource() : null;
    var current = (window.myAigaCart && window.myAigaCart.attributes) || {};

    if (
      current.myaiga_status === status &&
      current.myaiga_member === String(status === "member") &&
      current.myaiga_version === VERSION
    ) {
      return Promise.resolve(); // already in sync, avoid a redundant write
    }

    syncing = true;
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: Object.assign({}, current, {
          myaiga_status: status,
          myaiga_member: String(status === "member"),
          myaiga_source: source || "",
          myaiga_version: VERSION,
        }),
      }),
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        setCartState(cart);
        log("synced cart attributes:", status, source);
      })
      .catch(function (err) {
        log("attribute sync failed (non-fatal):", err.message);
      })
      .finally(function () {
        syncing = false;
      });
  }

  // ---------------------------------------------------------------------
  // Public cart API
  // ---------------------------------------------------------------------
  window.myAigaCartApi = {
    addToCart: function (payload) {
      return fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      }).then(function (res) {
        if (!res.ok) throw new Error("add to cart failed: " + res.status);
        return res.json();
      });
    },
    updateCart: function (payload) {
      return fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      }).then(function (res) {
        if (!res.ok) throw new Error("update cart failed: " + res.status);
        return res.json();
      });
    },
    getState: function () {
      return window.myAigaCart || null;
    },
  };

  // ---------------------------------------------------------------------
  // Detect the theme's own cart mutations WITHOUT touching window.fetch.
  // Overriding a global like window.fetch sitewide risks interfering with
  // any other script that inspects or relies on it being the untouched
  // native function — including, plausibly, this theme's own license/
  // integrity checks, which is exactly the kind of thing many commercial
  // themes verify. A MutationObserver watches the actual cart DOM instead
  // — it only reacts to visible changes in cart-related markup, and never
  // touches anything outside myAiga's own code path.
  // ---------------------------------------------------------------------
  var observerDebounce = null;
  function scheduleCartUpdatedFromDom() {
    if (syncing) return;
    clearTimeout(observerDebounce);
    observerDebounce = setTimeout(function () {
      window.dispatchEvent(new CustomEvent(window.myAiga ? window.myAiga.EVENTS.CART_UPDATED : "myaiga:cart-updated"));
    }, 150);
  }

  function watchableCartRoots() {
    // Cart page, cart drawer/minicart, and the plan selector's own
    // section wrapper are the only places cart contents actually change
    // in the DOM. Watching these specifically (rather than all of
    // document.body) keeps this cheap and avoids reacting to unrelated
    // page churn (carousels, animations, etc).
    return document.querySelectorAll(
      '[data-myaiga-plan-selector], .cart-drawer, #cart-drawer, .mini-cart, #mini-cart, [data-cart-items], .cart__items, form[action^="/cart"]'
    );
  }

  if (!window.__myAigaCartObserverBound) {
    window.__myAigaCartObserverBound = true;
    var observer = new MutationObserver(function () {
      scheduleCartUpdatedFromDom();
    });
    document.addEventListener("DOMContentLoaded", function () {
      watchableCartRoots().forEach(function (root) {
        observer.observe(root, { childList: true, subtree: true });
      });
    });
    // Also watch anything myAiga itself swaps in later (e.g. a cart
    // drawer that opens after page load) by re-scanning periodically at
    // a very low frequency — cheap, and only a fallback.
    setInterval(function () {
      watchableCartRoots().forEach(function (root) {
        if (!root.dataset.myaigaObserved) {
          root.dataset.myaigaObserved = "true";
          observer.observe(root, { childList: true, subtree: true });
        }
      });
    }, 3000);
  }

  window.addEventListener("myaiga:cart-updated", function () {
    fetchCart().then(function (cart) {
      setCartState(cart);
      waiveServiceFeeIfMember();
      bindCheckoutButtons();
      maybeAutoApplyDiscount();
    });
  });

  window.addEventListener("myaiga:membership-updated", function () {
    syncMembershipAttributes();
    waiveServiceFeeIfMember();
    // V7 handles checkout discount globally; legacy auto-apply disabled.
  });

  document.addEventListener("DOMContentLoaded", function () {
    // The Woomen theme's product-form publishes PUB_SUB_EVENTS.cartUpdate
    // after every successful AJAX add-to-cart. Subscribe to that event so
    // the $10 Service Fee is enforced immediately when a normal product is
    // added — not only when the customer happens to reload /cart.
    var cartUpdateSubscribed = false;
    if (typeof window.subscribe === "function" && typeof window.PUB_SUB_EVENTS !== "undefined") {
      // global.js declares subscribe/PUB_SUB_EVENTS as lexical globals, so
      // they are available here after DOMContentLoaded even though both
      // scripts are deferred.
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        if (syncing) return;
        fetchCart().then(function (cart) {
          setCartState(cart);
          return waiveServiceFeeIfMember();
        }).then(function () {
          maybeAutoApplyDiscount();
        });
      });
      cartUpdateSubscribed = true;
      log("subscribed to theme cart-update for fee enforcement");
    }

    // Also retain the myAiga custom event path used by the plan selector and
    // other custom cart components.
    fetchCart().then(function (cart) {
      setCartState(cart);
      syncMembershipAttributes();
      return waiveServiceFeeIfMember();
    }).then(function () {
      bindCheckoutButtons();
      maybeAutoApplyDiscount();
    });

    // Some integrations use a native form POST rather than the theme's
    // product-form component. A short, bounded re-check after initial load
    // catches that case without polling indefinitely.
    setTimeout(function () {
      fetchCart().then(function (cart) {
        setCartState(cart);
        return waiveServiceFeeIfMember();
      });
    }, 1200);
  });
})(window, document);
